import type { PublicInvoice } from '@/types/public';
import type { Invoice } from '@/types/invoice';

/**
 * Map the public invoice payload to the internal `Invoice` shape the document
 * renderers consume. Shared by BOTH customer-facing invoice surfaces — the PDF
 * viewer (`pdf-invoice.$id`) and the hosted pay page (`invoice.$id`) — so the
 * design document and its PDF read the invoice from one identical adapter and
 * cannot describe the same invoice two ways.
 */
export function toInvoice(pub: PublicInvoice): Invoice {
  return {
    id: pub.id,
    invoiceNumber: pub.invoiceNumber,
    status: pub.status,
    // Job facts the invoice document may print (parity with the classic page):
    // service address, job #, job type and the service date(s). Absent on a
    // manual invoice with no job — each block renders nothing then.
    jobTypeName: pub.job?.jobType ?? null,
    jobNumber: pub.job?.jobNumber ?? null,
    serviceAddress: pub.job?.serviceAddress ?? null,
    appointmentDates: pub.job?.appointmentDates ?? null,
    contact: pub.contact
      ? {
          id: '',
          firstName: pub.contact.firstName,
          lastName: pub.contact.lastName,
          primaryEmail: pub.contact.email,
          primaryPhone: pub.contact.phone,
          company: pub.contact.company,
        }
      : undefined,
    customFields: pub.customFields ?? [],
    // The template renderer reads billToContact for the bill-to name/label/
    // address; without it a catalog bill-to or overridden billing address
    // regresses to the raw contact on the customer-facing PDF.
    billToContact: pub.billToContact ?? null,
    lineItems: pub.lineItems.map((li) => ({
      id: li.id,
      name: li.name,
      description: li.description,
      quantity: li.quantity,
      unitPrice: li.unitPrice,
      discountPercent: li.discountPercent,
      taxRate: li.taxRate,
      lineTotal: li.lineTotal,
      // Pass through frozen tax snapshot so the PDF can render per-tax-code
      // registration numbers (NOT-7) alongside each tax row.
      taxSnapshot: li.taxSnapshot ?? null,
      // NOT-226 — billing basis, so a time & materials line prints its hours
      // and hourly rate instead of "Qty 1" at the collapsed line subtotal.
      type: li.type ?? undefined,
      unit: li.unit ?? undefined,
      priceStrategy: li.priceStrategy ?? undefined,
      estimatedHours: li.estimatedHours ?? undefined,
      actualHours: li.actualHours ?? undefined,
      laborRate: li.laborRate ?? undefined,
      // What this line CHARGES and whether it may be PRINTED. Without these the
      // PDF's `customerVisibleRows` runs on lines that have lost the fields it
      // asks about, answers the default for every one, and prints a
      // non-chargeable material the HTML page correctly hides.
      chargeable: li.chargeable ?? undefined,
      pricingTreatment: li.pricingTreatment ?? undefined,
      visibleToCustomer: li.visibleToCustomer ?? undefined,
      hiddenFromCustomer: li.hiddenFromCustomer ?? undefined,
      // The pre-flag spelling, which `visibleToCustomerFlag` still honours.
      showInDetail: li.showInDetail ?? undefined,
      // NOT-590 — without the linkage the rollup cannot group a service with
      // its parts, so an approved one-price service itemises on the PDF.
      lineKey: li.lineKey ?? undefined,
      parentLineKey: li.parentLineKey ?? undefined,
      componentDisplayMode: li.componentDisplayMode ?? undefined,
    })),
    notes: pub.notes,
    terms: pub.terms,
    subtotal: pub.subtotal,
    discountTotal: pub.discountTotal,
    // #2817 — membership savings, surfaced as its own line on the document.
    membershipDiscountTotal: pub.membershipDiscountTotal,
    taxTotal: pub.taxTotal,
    tipAmount: pub.tipAmount,
    total: pub.total,
    amountPaid: pub.amountPaid,
    // Same allowlist trap as `normalizeInvoice`: the public payload carries all
    // three, and dropping them here is why the customer's own copy of the
    // invoice — the hosted page AND the downloadable PDF — never printed the
    // deposit split or the card fee that #2913 added.
    depositApplied: pub.depositApplied,
    depositServiceFeeAmount: pub.depositServiceFeeAmount,
    paymentServiceFeeAmount: pub.paymentServiceFeeAmount,
    // Carried so the templated PDF's "Credits Applied" money line matches the
    // office PDF instead of always reading $0 on the public download.
    creditsApplied: pub.creditsApplied,
    balanceDue: pub.balanceDue,
    // Without these the customer's documents cannot print Paid* and its footnote.
    clearingBankTotal: pub.clearingBankTotal ?? null,
    clearingBankCash: pub.clearingBankCash ?? null,
    provisionalClearingTotal: pub.provisionalClearingTotal ?? null,
    clearingSince: pub.clearingSince ?? null,
    payments: [],
    date: pub.date,
    dueDate: pub.dueDate,
    createdAt: pub.createdAt,
    updatedAt: pub.updatedAt,
  };
}
